import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/core/components/ui/alert';

interface GridErrorStateProps {
  error: string;
  onRetry?: () => void;
  onClearError?: () => void;
  title?: string;
}

/**
 * Error state component for the kiosk sessions grid
 * Displayed when API calls fail or authentication issues occur
 */
export const GridErrorState = ({ 
  error,
  onRetry,
  onClearError,
  title = "Unable to Load Sessions"
}: GridErrorStateProps) => {
  const isAuthError = error.toLowerCase().includes('authentication') || 
                      error.toLowerCase().includes('session expired') ||
                      error.toLowerCase().includes('unauthorized');

  return (
    <div className="w-full">
      <Alert variant="destructive" className="mb-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>{title}</AlertTitle>
        <AlertDescription>
          {error}
        </AlertDescription>
      </Alert>

      <div className="border rounded-md">
        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div className="bg-red-100 rounded-full p-4 mb-4">
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isAuthError ? 'Authentication Required' : 'Data Loading Failed'}
          </h3>
          
          <p className="text-gray-600 mb-6 max-w-md">
            {isAuthError 
              ? 'Your session may have expired. Please refresh the page to re-authenticate.'
              : 'There was a problem loading the session data. Please try again.'
            }
          </p>
          
          <div className="flex space-x-3">
            {onRetry && (
              <Button 
                onClick={onRetry} 
                variant="default"
                className="flex items-center space-x-2"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Retry</span>
              </Button>
            )}
            
            {isAuthError && (
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline"
                className="flex items-center space-x-2"
              >
                <span>Refresh Page</span>
              </Button>
            )}
            
            {onClearError && !isAuthError && (
              <Button 
                onClick={onClearError} 
                variant="outline"
              >
                Dismiss
              </Button>
            )}
          </div>
          
          {isAuthError && (
            <div className="mt-4 text-xs text-gray-500">
              <p>If the problem persists, please contact your system administrator.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};